/**
 * AuditItemDecisions Component
 *
 * Handles the decisions section including statistics, DataList, and toggle functionality.
 * Extracted from AuditItem.tsx to reduce complexity and improve maintainability.
 */

import type { GraphQLAuditItem, GraphQLAuditDecision } from '@internal-audit/domain/graphql-types';
import { Button, ButtonSize, ButtonType, Accept } from '@soteria/design-system-ve';
import { useState, useCallback, useMemo, useEffect } from 'react';

import { DataList, MessageToast } from '../../../../core/components';
import type { ActionConfig } from '../../../../core/components/DataList';
import { useDeleteAuditDecision } from '../../../hooks/useDeleteAuditDecision';
import { useSelectAuditDecision } from '../../../hooks/useSelectAuditDecision';
import { createAuditDecisionColumns } from '../columns/AuditDecisionColumns';

interface AuditItemDecisionsProps {
  item: GraphQLAuditItem;
  decisions: GraphQLAuditDecision[];
  onDecisionSelect?: (decisionId: string) => void;
  isDecisionLoading: boolean;
  isSessionCompleted?: boolean;
}

/**
 * Helper function to determine tooltip text for select action
 */
const getSelectTooltip = (decision: GraphQLAuditDecision, selectLoading: boolean, isSessionCompleted: boolean) => {
  if (isSessionCompleted) return 'Session completed - read only';
  if (selectLoading) return 'Selecting...';

  return decision.isSelected ? 'Already selected' : 'Select this decision';
};

// eslint-disable-next-line complexity, max-statements
export function AuditItemDecisions({ item, decisions, onDecisionSelect, isDecisionLoading, isSessionCompleted = false }: AuditItemDecisionsProps) {
  const [showAllDecisions, setShowAllDecisions] = useState(false);
  const [dismissedErrorId, setDismissedErrorId] = useState<string | null>(null);

  // Initialize hooks for delete and select operations
  const { deleteAuditDecision, errors: deleteErrors } = useDeleteAuditDecision();
  const { selectAuditDecision, loading: selectLoading, errors: selectErrors } = useSelectAuditDecision();

  // Combine all errors from both hooks
  const allErrors = useMemo(() => [...deleteErrors, ...selectErrors], [deleteErrors, selectErrors]);

  // Get the first error for display (we'll show one toast at a time)
  const currentError = allErrors.length > 0 ? allErrors[0] : null;

  // Create a unique error ID based on error content to track dismissals
  const currentErrorId = currentError ? `${currentError.code}-${currentError.correlationId || 'no-correlation'}` : null;

  // Determine if we should show the error toast
  const shouldShowErrorToast = currentError && currentErrorId !== dismissedErrorId;

  // Handle toast dismissal - remember which error was dismissed
  const handleToastDismiss = useCallback(() => {
    if (currentErrorId) {
      setDismissedErrorId(currentErrorId);
    }
  }, [currentErrorId]);

  // Clear dismissed error ID when errors clear (new operation starts)
  useEffect(() => {
    if (allErrors.length === 0) {
      setDismissedErrorId(null);
    }
  }, [allErrors]);

  // Separate current user's decisions from others
  const { currentUserDecisions, otherDecisions } = useMemo(() => {
    const currentUserDecisions = decisions.filter(decision => decision.isCurrentUser);
    const otherDecisions = decisions.filter(decision => !decision.isCurrentUser);

    return { currentUserDecisions, otherDecisions };
  }, [decisions]);

  // Determine which decisions to display
  const displayedDecisions = useMemo(() => {
    if (showAllDecisions) {
      return decisions;
    }

    // Show current user's decisions by default, or if no current user decisions exist,
    // show an empty array (the user will see the "Show All Decisions" button)
    return currentUserDecisions.length > 0 ? currentUserDecisions : [];
  }, [decisions, currentUserDecisions, showAllDecisions]);

  const hasMoreDecisions = otherDecisions.length > 0 || currentUserDecisions.length > 1;

  // Handle decision selection callback
  const handleDecisionSelect = useCallback(
    (decisionId: string) => {
      onDecisionSelect?.(decisionId);
    },
    [onDecisionSelect]
  );

  // Handle decision selection
  const handleSelect = useCallback(
    async (decision: GraphQLAuditDecision) => {
      if (isSessionCompleted) {
        return; // Don't allow selection for completed sessions
      }

      try {
        const result = await selectAuditDecision({
          input: {
            decisionId: decision.id,
            auditItemId: item.id,
            auditSessionId: item.auditSessionId,
          },
        });

        if (result.success && 'data' in result && result.data) {
          // Call the legacy callback to trigger a refresh
          handleDecisionSelect(decision.id);
        }
      } catch {
        // Error is handled by the hook's error state and authErrorManager
      }
    },
    [selectAuditDecision, item.id, item.auditSessionId, handleDecisionSelect, isSessionCompleted]
  );

  // Handle decision deletion with optimistic updates
  const handleDelete = useCallback(
    async (decision: GraphQLAuditDecision) => {
      if (isSessionCompleted) {
        return false; // Don't allow deletion for completed sessions
      }

      // Start the deletion process (optimistic updates happen in the hook)
      deleteAuditDecision({
        input: {
          decisionId: decision.id,
          auditItemId: item.id,
          auditSessionId: item.auditSessionId,
        },
      })
        .then(result => {
          if (result.success && 'data' in result && result.data) {
            // Call the legacy callback to trigger a refresh
            handleDecisionSelect(decision.id);
          }
        })
        .catch(() => {
          // Error is handled by the hook's error state and authErrorManager
        });

      // Return true immediately to close dialog (optimistic behavior)
      return true;
    },
    [deleteAuditDecision, item.id, item.auditSessionId, handleDecisionSelect, isSessionCompleted]
  );

  // Get table columns from extracted module - use classification results with severity
  const classificationResults = item.contentSnapshot?.classificationResults || [];
  const contentType = item.contentSnapshot?.contentType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns = createAuditDecisionColumns(classificationResults, contentType) as any; // Type assertion needed due to decision type mismatch

  // Define additional action for select
  const selectAction: ActionConfig<GraphQLAuditDecision> = {
    icon: Accept,
    label: 'Select',
    onClick: handleSelect,
    buttonType: (decision: GraphQLAuditDecision) => (decision.isSelected ? ButtonType.Brand : ButtonType.Primary),
    disabled: (decision: GraphQLAuditDecision) => decision.isSelected || selectLoading || isSessionCompleted,
    tooltip: (decision: GraphQLAuditDecision) => getSelectTooltip(decision, selectLoading, isSessionCompleted),
  };

  // Define delete item context without error handling - errors are now shown via MessageToast
  const deleteItemContext = isSessionCompleted
    ? undefined
    : {
        handler: handleDelete,
        isDeleting: false, // Optimistic updates - no loading state needed
        errors: null, // Remove error display from DataList
        resetErrors: () => {
          // Errors are managed by the hooks and authErrorManager
          // No manual reset needed as they auto-clear on next operation
        },
        getItemDisplayName: (decision: GraphQLAuditDecision) => (decision.isCurrentUser ? 'your decision' : `created by ${decision.auditorName}`),
      };

  return (
    <div>
      {/* Show error toast if deletion failed and not dismissed */}
      {shouldShowErrorToast && currentError && (
        <MessageToast
          ariaLabel={`Deletion error: ${currentError.userMessage}`}
          isVisible={shouldShowErrorToast}
          message={currentError.userMessage}
          subMessage={`Error Code: ${currentError.code}${currentError.correlationId ? `\nCorrelation ID: ${currentError.correlationId}` : ''}`}
          variant="error"
          onClose={handleToastDismiss}
        />
      )}

      {/* Always show DataList to display loading state even when no decisions exist */}
      <DataList
        additionalAction={selectAction}
        columns={columns}
        data={displayedDecisions}
        dataTypeName="decision"
        deleteItemContext={deleteItemContext}
        emptyStateMessage={decisions.length === 0 ? 'No decisions have been made yet.' : 'You have not made any decisions yet.'}
        hideEmptyState={false}
        isLoading={isDecisionLoading}
        loadingRowCount={1}
        variant="condensed"
      />

      {hasMoreDecisions && (
        <div style={{ marginTop: '12px', textAlign: 'center' }}>
          <Button
            buttonSize={ButtonSize.Accelerator}
            buttonType={ButtonType.Tertiary}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation(); // Prevent card selection
              setShowAllDecisions(!showAllDecisions);
            }}
          >
            {showAllDecisions ? 'Show My Decisions Only' : `Show All Decisions (${decisions.length - displayedDecisions.length} more)`}
          </Button>
        </div>
      )}
    </div>
  );
}
